import camelcaseKeys from "camelcase-keys";
import { AxiosError, AxiosResponse } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AXIOS } from "../axios";
import { useAuth } from "../../components/auth/AuthProvider";
import { useError } from "../../components/status/ErrorProvider";
import { useSuccess } from "../../components/status/SuccessProvider";
import { MetaSchema, PaginationSchema } from "../pagination";

export type Project = {
  id: string;
  name: string;
  tags: string[];
  users: string[];
  online: boolean;
  acronym: string;
  lastActive: Date;
  description: string;
  activeUsers: number;
  latestUseId: number;
};

type useProxyProjectsSchema = PaginationSchema & { name?: string };

export const useProxyProjects = (body?: useProxyProjectsSchema) => {
  const url = useAuth().user.activeOrganization.url;

  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    { data: Project[]; meta: MetaSchema }
  >({
    queryKey: [`PROXY PROJECTS ${JSON.stringify(body)}`],
    queryFn: () => AXIOS.post(`${url}/projects`, body),
    select: (data) => camelcaseKeys(data.data, { deep: true }),
    staleTime: 1000 * 60 * 2,
  });
};

type useProxyProjectCreateSchema = {
  description: string;
  projectName: string;
  projectAcronym: string;
};
export const useProxyProjectCreate = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    useProxyProjectCreateSchema
  >({
    mutationKey: [`PROXY PROJECT CREATE`],
    mutationFn: (props: useProxyProjectCreateSchema) =>
      AXIOS.post(`${url}/project/create`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Project created`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY PROJECTS"),
      });
    },
  });
  return mutation;
};

type useProxyProjectRemoveSchema = { id: string };

export const useProxyProjectRemove = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    useProxyProjectRemoveSchema
  >({
    mutationKey: [`PROXY PROJECT REMOVE`],
    mutationFn: (props: useProxyProjectRemoveSchema) =>
      AXIOS.post(`${url}/project/remove`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Project removed`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY PROJECTS"),
      });
    },
  });
  return mutation;
};

type DescriptionSchema = { id: string; description: string };

export const useProxyProjectEditDescription = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    DescriptionSchema
  >({
    mutationKey: [`PROXY PROJECT EDIT DESCRIPTION`],
    mutationFn: (props: DescriptionSchema) =>
      AXIOS.post(`${url}/project/edit/description`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Description updated`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY PROJECTS"),
      });
    },
  });

  return mutation;
};

type ProjectTagsUpdateSchema = { id: string; tags: string[] };

export const useProxyProjectEditTags = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<Project, any>,
    AxiosError<{ error: string; message: string }>,
    ProjectTagsUpdateSchema
  >({
    mutationKey: [`PROXY PROJECT EDIT TAGS`],
    mutationFn: (props: ProjectTagsUpdateSchema) =>
      AXIOS.post(`${url}/project/edit/tags`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Tags updated`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY PROJECTS"),
      });
    },
  });

  return mutation;
};

type useProxyProjectResetSchema = { id: string };

export const useProxyProjectReset = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    useProxyProjectResetSchema
  >({
    mutationKey: [`PROXY PROJECT RESET`],
    mutationFn: (props: useProxyProjectResetSchema) =>
      AXIOS.post(`${url}/project/reset`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Project artifacts reset`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY KEYS"),
      });
    },
  });

  return mutation;
};

type ProjectMgmt = {
  userIds: readonly string[];
  projectId: string;
  comment?: string;
};

export const useProxyProjectsAssign = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    ProjectMgmt
  >({
    mutationKey: [`PROXY PROJECTS ASSIGN`],
    mutationFn: (props: ProjectMgmt) =>
      AXIOS.post(`${url}/projects/assign`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Project assigned`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY USERS"),
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY PROJECTS"),
      });
    },
  });

  return mutation;
};

export const useProxyProjectsUnassign = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    ProjectMgmt
  >({
    mutationKey: [`PROXY PROJECTS UNASSIGN`],
    mutationFn: (props: ProjectMgmt) =>
      AXIOS.post(`${url}/projects/unassign`, props),
    onError: (error) => {
      const data = error?.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess(`Project unassigned`);

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY USERS"),
      });

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY PROJECTS"),
      });
    },
  });

  return mutation;
};

type ProjectState = { id: string };

export const useProxyProjectsActivate = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    ProjectState
  >({
    mutationKey: [`PROXY PROJECTS ACTIVATE`],
    mutationFn: (props: ProjectState) =>
      AXIOS.post(`${url}/projects/activate`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess("Project activated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY USERS"),
      });

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY PROJECTS"),
      });
    },
  });

  return mutation;
};

export const useProxyProjectsDeactivate = () => {
  const url = useAuth().user.activeOrganization.url;

  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ error: string; message: string }>,
    ProjectState
  >({
    mutationKey: [`PROXY PROJECTS DEACTIVATE`],
    mutationFn: (props: ProjectState) =>
      AXIOS.post(`${url}/projects/deactivate`, props),
    onError: (error) => {
      const data = error.response?.data;
      const message = data?.error ?? data?.message ?? "please try again";
      setError({ message, reset: mutation.reset });
    },
    onSuccess: (data) => {
      setSuccess("Project deactivated");

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY USERS"),
      });

      queryClient.invalidateQueries({
        predicate: (query) =>
          // @ts-ignore
          query.queryKey[0]?.startsWith("PROXY PROJECTS"),
      });
    },
  });

  return mutation;
};
